import dayjs from "dayjs";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import {
  InputDateControllerRow,
  InputTextRow,
  SelectControllerRow,
} from "@components";

import { useEmployeeServiceData } from "@hooks/useEmployeeServiceData";
import { useLanguage } from "@hooks/useLanguage";

import { getEmployeeServiceDataConvert } from "@helpers/employeeServiceDataConvert";
import { getConvertedFunctionalityData } from "@helpers/functionalityDataConvert";
import { getConvertedMasterData } from "@helpers/masterDataConvert";

import { EMPLOYEE_SERVICE_DATA } from "@constants/employee-service-data";

import { calculateDaysDifference } from "@utils/duration";
import {
  ApplyLeaveFormDataType,
  BASIC_INFO,
} from "@validations/HRM/leave/applyLeaveFormValidation";

interface BasicInfoPropType {
  unitId: string | number;
  leaveType: any;
  leaveNature: any;
  isApplyBtnDisabled: boolean;
  setIsApplyBtnDisabled: (arg: boolean) => void;
  employeeId: string | number;
}

export const BasicInfo = ({
  unitId,
  leaveType,
  leaveNature,
  isApplyBtnDisabled,
  setIsApplyBtnDisabled,
  employeeId,
}: BasicInfoPropType) => {
  const { language } = useLanguage();

  const {
    register,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<ApplyLeaveFormDataType>();

  const selectedLeaveType = watch(BASIC_INFO.LEAVE_TYPE);
  const startDate = watch(BASIC_INFO.FROM_DATE);
  const endDate = watch(BASIC_INFO.TO_DATE);

  const totalLeave = calculateDaysDifference(
    startDate as string,
    endDate as string,
  );

  useEffect(() => {
    if (
      startDate &&
      endDate &&
      leaveType?.find((item: any) => item.id === selectedLeaveType)
        ?.maxDuration < totalLeave
    ) {
      setIsApplyBtnDisabled(true);
    } else {
      setIsApplyBtnDisabled(false);
    }
  }, [totalLeave, selectedLeaveType]);

  const { data: commonInfoResponse } = useEmployeeServiceData({
    dataType: EMPLOYEE_SERVICE_DATA.COMMON_INFO,
    unitId,
  });
  const commonInfo = commonInfoResponse?.commonInfo || [];

  return (
    <div className="d-flex flex-column gap-8 pb-8">
      <SelectControllerRow
        label="LEAVE_APPLY.FORM.BASIC_INFO.LEAVE_TYPE"
        name={BASIC_INFO.LEAVE_TYPE}
        register={{ ...register(BASIC_INFO.LEAVE_TYPE) }}
        options={getConvertedFunctionalityData(leaveType, language)}
        setValue={setValue}
        control={control}
        required
      />
      <SelectControllerRow
        label="LEAVE_APPLY.FORM.BASIC_INFO.LEAVE_NATURE"
        name={BASIC_INFO.LEAVE_NATURE}
        register={{ ...register(BASIC_INFO.LEAVE_NATURE) }}
        options={getConvertedMasterData(leaveNature, language)}
        setValue={setValue}
        control={control}
        required
      />
      <InputDateControllerRow
        label="LEAVE_APPLY.FORM.BASIC_INFO.FROM_DATE"
        control={control}
        errors={errors}
        name={BASIC_INFO.FROM_DATE}
        maximumDate={endDate && (dayjs(endDate as string) as any)}
        required
      />
      <InputDateControllerRow
        label="LEAVE_APPLY.FORM.BASIC_INFO.TO_DATE"
        control={control}
        errors={errors}
        name={BASIC_INFO.TO_DATE}
        restriction={isApplyBtnDisabled}
        minimumDate={
          dayjs().isAfter(startDate as string)
            ? dayjs(startDate as string)
            : dayjs()
        }
        required
      />
      <SelectControllerRow
        label="LEAVE_APPLY.FORM.BASIC_INFO.DELEGATED_EMPLOYEE_NAME"
        name={BASIC_INFO.DELEGATED_EMPLOYEE_NAME}
        register={{ ...register(BASIC_INFO.DELEGATED_EMPLOYEE_NAME) }}
        options={getEmployeeServiceDataConvert(
          commonInfo,
          language,
          employeeId,
        )}
        setValue={setValue}
        control={control}
        required
        isSearchable
      />
      <InputTextRow
        register={{ ...register(BASIC_INFO.ADDRESS) }}
        label="LEAVE_APPLY.FORM.BASIC_INFO.ADDRESS"
        error={errors}
        required
      />
      <InputTextRow
        register={{ ...register(BASIC_INFO.REASON) }}
        label="LEAVE_APPLY.FORM.BASIC_INFO.REASON"
        error={errors}
      />
    </div>
  );
};
